import React, { useMemo } from "react";
import { Tabs } from "@shared/ui/tabs";
import { AlertCircle, CreditCard, RotateCcw } from "lucide-react";
import { Button } from "@shared/ui/button";
import { MOCK_BILLS, MOCK_PAYMENTS } from "./mockData";
import AllBills from "./tabs/AllBills";
import UnPaid from "./tabs/UnPaid";
import Paid from "./tabs/Paid";
import PaymentHistory from "./tabs/PaymentHistory";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { useNavigate } from "react-router-dom";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useAllAccounts } from "../../../../shared/hook";

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currency = useCurrency();
  const unpaidBills = MOCK_BILLS.filter((b) => b.status === "Unpaid");
  const paidBills = MOCK_BILLS.filter((b) => b.status === "Paid");
  
  const totalUnpaidAmount = unpaidBills.reduce((acc, bill) => {
    const amt = parseFloat(bill.amount.replace(/[^0-9.-]+/g, ""));
    return acc + (isNaN(amt) ? 0 : amt);
  }, 0);
  
  const accountsWithUnpaidBills = new Set(unpaidBills.map(b => b.account)).size;

  const { data: allAccounts } = useAllAccounts({
    page: 1,
    limit: 1000,
    active_consumers: 1,
    remote_utility_id: getRemoteUtilityId(),
    is_sub_account: 1,
    consumer_id: getLoginDataFromStorage().consumerId,
  });

  const allConsumerNos = useMemo(() => {
    return allAccounts?.results?.map((acc: any) => acc.consumerNo).join(',') || '';
  }, [allAccounts]);

  return (
    <div className="space-y-6 mt-6">
      {/* Alert Banner */}
      <div className="bg-red-50/80 border border-red-100 rounded-lg p-3 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <div className="flex flex-col gap-0.5">
            <span className="font-bold text-red-700 text-sm">
              {t("{{count}} unpaid bills - Total: {{amount}}", { count: 'NA', amount: `${currency} ${'NA'}` })}
            </span>
            <span className="text-xs text-red-600/80 font-medium">
              {t("Across {{count}} account(s)", { count: accountsWithUnpaidBills })}
            </span>
          </div>
        </div>
        <Button 
          className="bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center gap-2 h-9 px-4 font-semibold"
          onClick={() => navigate(`/dashboard/billing/pay?type=bills&isAllAccounts=true&consumer_no=${allConsumerNos}`)}
        >
          <CreditCard className="w-4 h-4" /> {t("Pay All Now →")}
        </Button>
      </div>

      {/* Sub Tabs */}
      <Tabs
        defaultValue="all-bills"
        disableUrlSync={true}
        tabComponents={{
          "all-bills": {
            label: t("All Bills"),
            // count: MOCK_BILLS.length,
            component: <AllBills />
          },
          "unpaid": {
            label: t("Unpaid"),
            // count: unpaidBills.length,
            component: <UnPaid />
          },
          "paid": {
            label: t("Paid"),
            // count: paidBills.length,
            component: <Paid />
          },
          "history": {
            label: t("Payment History"),
            component: <PaymentHistory />
          }
        }}
      />
    </div>
  );
}
